import {
    CheckCircleFilled,
    CloseCircleFilled,
    DownloadOutlined,
    EyeOutlined,
    LoadingOutlined,
    MergeCellsOutlined,
    RocketOutlined,
    SyncOutlined,
    ThunderboltOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import {
    Alert, Button, Card, Col, Progress, Row, Space, Statistic, Steps, Table, Tag, Typography,
} from "antd";
import * as XLSX from "xlsx";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import PageLayout from "../../components/PageLayout";
import axiosInstance from "../../variables/api/axiosInstance";

const { Title, Text } = Typography;
const POLL_INTERVAL_MS = 2500;
const ETAT_LABELS = { EN_COURS: "En cours", TERMINE: "Terminé", ERREUR: "Erreur", INCONNU: "Inconnu" };

export default function RepriseSinistreFusion() {
    const [step, setStep]         = useState(0);
    const [apercu, setApercu]     = useState(null);
    const [rapport, setRapport]   = useState(null);
    const [loading, setLoading]   = useState(false);
    const [progress, setProgress] = useState(0);
    const [jobId, setJobId]       = useState(null);
    const [jobEtat, setJobEtat]   = useState(null);
    const pollRef = useRef(null);

    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    const previsualiser = async () => {
        setLoading(true); setApercu(null);
        try {
            const res = await axiosInstance.post(
                "/api/migration/reprise-sinistres/fusion-doublons/preview", {});
            setApercu(res.data); setStep(1);
        } catch (err) {
            toast.error("Erreur aperçu : " + (err.response?.data?.message || err.message));
        } finally { setLoading(false); }
    };

    const appliquer = async () => {
        setLoading(true); setRapport(null); setProgress(5); setJobEtat("EN_COURS"); setStep(2);
        try {
            const res = await axiosInstance.post(
                "/api/migration/reprise-sinistres/fusion-doublons", {});
            const id = res.data?.jobId;
            if (!id) throw new Error("Aucun jobId reçu du serveur.");
            setJobId(id); demarrerPolling(id);
        } catch (err) {
            setLoading(false); setJobEtat("ERREUR");
            toast.error("Erreur : " + (err.response?.data?.message || err.message));
        }
    };

    const demarrerPolling = useCallback((id) => {
        let ticks = 0;
        pollRef.current = setInterval(async () => {
            ticks++;
            setProgress(p => Math.min(p + Math.max(1, 5 - Math.floor(ticks / 5)), 90));
            try {
                const res = await axiosInstance.get(`/api/migration/job/${id}`);
                const statut = res.data;
                if (statut.etat === "TERMINE" || statut.etat === "ERREUR") {
                    clearInterval(pollRef.current);
                    setProgress(100); setJobEtat(statut.etat); setLoading(false);
                    if (statut.etat === "TERMINE") {
                        setRapport(statut.resultat); setStep(3);
                        toast.success("Fusion des doublons terminée !");
                    } else {
                        toast.error("Erreur : " + (statut.erreur || "Erreur inconnue"));
                    }
                }
            } catch (err) {
                clearInterval(pollRef.current); setLoading(false); setJobEtat("ERREUR");
                toast.error("Erreur polling : " + err.message);
            }
        }, POLL_INTERVAL_MS);
    }, []);

    const exportRapport = (d) => {
        if (!d) return;
        const wb = XLSX.utils.book_new();
        const resume = [
            ["Indicateur", "Valeur"],
            ["Groupes de doublons", d.groupesDoublons],
            ["Doublons désactivés", d.dossiersDesactives],
            ["Décaissements re-pointés", d.decaissementsRepointes],
            ["Canoniques recalculés (SAP)", d.sinistresRecalcules],
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resume), "Résumé");
        const ech = [
            ["N° sinistre", "Dossiers dans le groupe", "Doublons désactivés", "Décaissements re-pointés", "Canonique (libellé)"],
            ...(d.echantillon || []).map(e => [e.numSinistre, e.dossiersDansGroupe, e.doublonsDesactives,
                e.decaissementsRepointes, e.canonique]),
        ];
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(ech), "Échantillon");
        XLSX.writeFile(wb, `fusion_doublons_sinistres_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const reinitialiser = () => {
        if (pollRef.current) clearInterval(pollRef.current);
        setStep(0); setApercu(null); setRapport(null);
        setLoading(false); setProgress(0); setJobId(null); setJobEtat(null);
    };

    const colsEch = [
        { title: "N° sinistre", dataIndex: "numSinistre", key: "numSinistre", width: 160,
          render: v => <Text code style={{ fontSize: 11 }}>{v}</Text> },
        { title: "Dossiers", dataIndex: "dossiersDansGroupe", key: "dossiersDansGroupe", width: 80, align: "center",
          render: v => <Tag>{v}</Tag> },
        { title: "Désactivés", dataIndex: "doublonsDesactives", key: "doublonsDesactives", width: 90, align: "center",
          render: v => <Tag color="volcano">{v}</Tag> },
        { title: "Décaiss. re-pointés", dataIndex: "decaissementsRepointes", key: "decaissementsRepointes", width: 130, align: "center",
          render: v => v > 0 ? <Tag color="green">{v}</Tag> : <Tag>0</Tag> },
        { title: "Canonique gardé", dataIndex: "canonique", key: "canonique",
          render: v => <Text type="secondary" style={{ fontSize: 12 }}>{v || "—"}</Text> },
    ];

    const Indics = ({ d }) => (
        <Row gutter={[16, 16]}>
            <Col xs={12} sm={8} md={6}>
                <Statistic title="Groupes de doublons" value={d.groupesDoublons}
                    valueStyle={{ fontSize: 18, color: "#242166" }} />
            </Col>
            <Col xs={12} sm={8} md={6}>
                <Statistic title="Doublons désactivés" value={d.dossiersDesactives}
                    valueStyle={{ color: "#fa541c", fontSize: 18 }} />
            </Col>
            <Col xs={12} sm={8} md={6}>
                <Statistic title="Décaissements re-pointés" value={d.decaissementsRepointes}
                    valueStyle={{ color: "#52c41a", fontSize: 18 }} prefix={<CheckCircleFilled />} />
            </Col>
            <Col xs={12} sm={8} md={6}>
                <Statistic title="Canoniques recalculés" value={d.sinistresRecalcules}
                    valueStyle={{ fontSize: 18, color: "#242166" }} />
            </Col>
        </Row>
    );

    return (
        <PageLayout currentMenu="REPRISE">
            <div style={{ padding: "24px 0" }}>
                <Space align="center" style={{ marginBottom: 24 }}>
                    <MergeCellsOutlined style={{ fontSize: 28, color: "#242166" }} />
                    <div>
                        <Title level={4} style={{ margin: 0, color: "#242166" }}>
                            Fusionner les dossiers-sinistres en doublon
                        </Title>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Sans fichier — contournement e-Force (dossiers minimaux auto-créés)
                        </Text>
                    </div>
                </Space>

                <Steps current={step} style={{ marginBottom: 32 }} items={[
                    { title: "Lancement" }, { title: "Aperçu" }, { title: "Application" }, { title: "Rapport" },
                ]} />

                {step === 0 && (
                    <Card>
                        <Alert type="info" showIcon style={{ marginBottom: 16 }}
                            message="Aucun fichier requis. Regroupe les dossiers par (n° de sinistre + police normalisée + date de survenance). Dans chaque groupe : garde le dossier déclaré le plus complet (pas le '[FORCE-REPRISE]' minimal), re-pointe les décaissements des doublons vers lui, désactive les doublons (réversible) et recalcule le SAP du canonique. L'aperçu ne modifie rien." />
                        <div style={{ textAlign: "right" }}>
                            <Button type="primary" size="large" icon={<EyeOutlined />} loading={loading}
                                onClick={previsualiser} style={{ background: "#242166" }}>
                                Prévisualiser
                            </Button>
                        </div>
                    </Card>
                )}

                {step === 1 && apercu && (
                    <div>
                        <Alert type="warning" showIcon style={{ marginBottom: 20 }}
                            message="Aperçu — aucune écriture effectuée"
                            description={`${apercu.groupesDoublons} groupe(s) de doublons — ${apercu.dossiersDesactives} dossier(s) seront désactivés, ${apercu.decaissementsRepointes} décaissement(s) re-pointés. Vérifiez l'échantillon puis appliquez.`} />
                        <Card style={{ marginBottom: 20 }}><Indics d={apercu} /></Card>
                        {apercu.echantillon?.length > 0 && (
                            <Card size="small" style={{ marginBottom: 20 }}
                                title={<Text strong>Échantillon des groupes ({apercu.echantillon.length})</Text>}>
                                <Table dataSource={apercu.echantillon.map((e, i) => ({ key: i, ...e }))}
                                    columns={colsEch} size="small" pagination={{ pageSize: 10, size: "small" }}
                                    scroll={{ y: 340 }} />
                            </Card>
                        )}
                        {apercu.lignesErreur?.length > 0 && (
                            <Card size="small" style={{ marginBottom: 20 }}
                                title={<Space><WarningOutlined style={{ color: "#faad14" }} />
                                    <Text strong>Anomalies ({apercu.lignesErreur.length})</Text></Space>}>
                                <Table dataSource={apercu.lignesErreur.map((m, i) => ({ key: i, message: m }))}
                                    columns={[{ title: "Détail", dataIndex: "message", key: "message",
                                        render: v => <Text type="warning" style={{ fontSize: 12 }}>{v}</Text> }]}
                                    size="small" pagination={{ pageSize: 15 }} scroll={{ y: 220 }} />
                            </Card>
                        )}
                        <Space>
                            <Button onClick={reinitialiser}>Annuler</Button>
                            <Button icon={<DownloadOutlined />} onClick={() => exportRapport(apercu)}>Exporter l'aperçu</Button>
                            <Button type="primary" icon={<ThunderboltOutlined />} disabled={apercu.groupesDoublons === 0}
                                onClick={appliquer} style={{ background: "#242166" }}>
                                Appliquer en base ({apercu.dossiersDesactives})
                            </Button>
                        </Space>
                    </div>
                )}

                {step === 2 && (
                    <Card>
                        <div style={{ textAlign: "center", padding: "40px 0" }}>
                            {loading ? (
                                <>
                                    <LoadingOutlined style={{ fontSize: 48, color: "#242166", marginBottom: 20 }} />
                                    <div style={{ marginBottom: 8 }}><Text strong style={{ fontSize: 16 }}>Fusion en cours…</Text></div>
                                    {jobId && (
                                        <div style={{ marginBottom: 12 }}>
                                            <Text type="secondary" style={{ fontSize: 11 }}>Job ID : <Text code>{jobId}</Text></Text>
                                            <Tag icon={<SyncOutlined spin />} color="processing" style={{ marginLeft: 8 }}>
                                                {ETAT_LABELS[jobEtat] || jobEtat}</Tag>
                                        </div>
                                    )}
                                    <Progress percent={progress} strokeColor="#242166" style={{ maxWidth: 500, margin: "0 auto" }} />
                                </>
                            ) : jobEtat === "ERREUR" ? (
                                <>
                                    <CloseCircleFilled style={{ fontSize: 48, color: "#ff4d4f", marginBottom: 20 }} />
                                    <div><Text type="danger" strong style={{ fontSize: 16 }}>L'opération a échoué.</Text></div>
                                    <Button onClick={reinitialiser} style={{ marginTop: 16 }}>Recommencer</Button>
                                </>
                            ) : null}
                        </div>
                    </Card>
                )}

                {step === 3 && rapport && (
                    <div>
                        <Alert type="success" showIcon icon={<CheckCircleFilled style={{ color: "#52c41a" }} />}
                            message={`${rapport.groupesDoublons} groupe(s) fusionnés — ${rapport.dossiersDesactives} doublon(s) désactivés, ${rapport.decaissementsRepointes} décaissement(s) re-pointés`}
                            description={`Durée : ${(rapport.dureeMs / 1000).toFixed(1)}s — réversible via m_sinistre_bak_fusion / m_decaissement_bak_fusion`} style={{ marginBottom: 20 }} />
                        <Card style={{ marginBottom: 20 }}><Indics d={rapport} /></Card>
                        {rapport.echantillon?.length > 0 && (
                            <Card size="small" style={{ marginBottom: 20 }}
                                title={<Text strong>Échantillon des groupes ({rapport.echantillon.length})</Text>}>
                                <Table dataSource={rapport.echantillon.map((e, i) => ({ key: i, ...e }))}
                                    columns={colsEch} size="small" pagination={{ pageSize: 10, size: "small" }}
                                    scroll={{ y: 340 }} />
                            </Card>
                        )}
                        <Space>
                            <Button icon={<DownloadOutlined />} onClick={() => exportRapport(rapport)}>Exporter le rapport</Button>
                            <Button type="primary" icon={<RocketOutlined />} onClick={reinitialiser} style={{ background: "#242166" }}>
                                Nouvelle fusion
                            </Button>
                        </Space>
                    </div>
                )}
            </div>
        </PageLayout>
    );
}
